import NextLink from "next/link";
import { useTranslation } from "next-i18next";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import BreadcrumbsComponent from "@atoms/Breadcrumbs";
import ButtonHelp from "@atoms/ButtonHelp";
import { ProductCard } from "@atoms/ProductCard";
import { Section } from "@atoms/Section";
import { IMG_TELKOM } from "@config/images";
import color from "@definitions/mui/color";
import Button from "@ds/components/Button";
import Text from "@ds/components/Text";
import RightArrow from "@icons/RightArrow";
import useResponsive from "@helpers/useResponsive";
import type { TPopularProduct } from "./type";
import { isAllowInterest } from "@config/feature";

const useStyles = makeStyles(() => ({
  title: {
    fontSize: 32,
    lineHeight: "50px",
    marginBottom: 20,
  },
}));

interface IPropsContainerPopularProduct {
  dataPopularProducts: TPopularProduct[];
}

const PopularProduct: React.FC<IPropsContainerPopularProduct> = ({ dataPopularProducts }) => {
  const isTablet = useResponsive("lg");
  const isMobile = useResponsive("md");
  const classes = useStyles();
  const { t } = useTranslation("common");
  const tabletPadding = isTablet ? "110px" : "auto";

  return (
    <>
      <Box position="relative" bgcolor="white" mt="70px" minHeight="100vh">
        <Box py={1}>
          <Box bgcolor={color.grey.mid} py={1} width="100%" px="16px">
            <Container maxWidth="lg" disableGutters>
              <BreadcrumbsComponent
                homeUrl={{ label: t("root"), productPath: "/" }}
                breadcrumbs={[{ label: t("popular.title") }]}
              />
            </Container>
          </Box>
        </Box>
        <Section bgcolor="white" maxWidth="full-width" pt={5} px={tabletPadding}>
          <Container maxWidth="lg" disableGutters>
            <Box
              display="flex"
              flexDirection="column"
              textAlign={isMobile ? "center" : "start"}
              mb={5}
            >
              <Box mb={1}>
                <Text weight="700" variant="h1" sx={{ fontSize: "32px" }}>
                  {t("popular.title")}
                </Text>
              </Box>
              <Text weight="400" color="general-mid">
                {t("popular.description")}
              </Text>
            </Box>
            <Grid
              container
              spacing={2}
              justifyContent={{ sm: "center", md: "flex-start" }}
              alignItems={{ sm: "center", md: "flex-start" }}
            >
              {dataPopularProducts.map((item: TPopularProduct, key: number) => {
                return (
                  <Grid item sm={6} md={3} lg={3} xl={3} key={key}>
                    <ProductCard
                      data-testid="product-click"
                      title={
                        <Text variant="h2" sx={{ fontSize: 18, lineHeight: "24px" }}>
                          {item.productName}
                        </Text>
                      }
                      image={
                        item.productYoutubeId
                          ? `https://img.youtube.com/vi/${item.productYoutubeId}/maxresdefault.jpg`
                          : item.productImage?.mediaPath
                      }
                      description={
                        <Box mb={1}>
                          <Text
                            noWrap
                            variant="body2"
                            color="blue-secondary"
                            sx={{ fontSize: "14px" }}
                          >
                            {item.parentProduct}
                          </Text>
                        </Box>
                      }
                      width="auto"
                      linkTo={item.productPath}
                      htmlDescription={item.story}
                      minHeight={348}
                    >
                      <Box position="absolute" right={14} bottom={8}>
                        <RightArrow style={{ color: color.primary.main }} />
                      </Box>
                    </ProductCard>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </Section>
        <Section id="interestSection" bgcolor="#F5F7FA" maxWidth="full-width" px={tabletPadding}>
          <Container maxWidth="lg" disableGutters>
            <Box
              paddingY={isMobile ? "32px" : "60px"}
              borderRadius="16px"
              sx={{
                textAlign: "center",
                // eslint-disable-next-line max-len
                background: `linear-gradient(rgba(0,0,0,60%),rgba(0,0,0,60%)), url(${IMG_TELKOM}) center/cover no-repeat`,
              }}
            >
              <Box maxWidth={680} margin="auto">
                <Text className={classes.title} variant="h2" color="white" weight="700">
                  {t("interest.title")}
                </Text>
                <Text component="p" variant="body1" color="white" sx={{ marginBottom: 20 }}>
                  {t("interest.desc")}
                </Text>
                {isAllowInterest && (
                  <NextLink href="/interest-form">
                    <a>
                      <Button size="large">{t("interest.button")}</Button>
                    </a>
                  </NextLink>
                )}
              </Box>
            </Box>
          </Container>
        </Section>
        <ButtonHelp />
      </Box>
    </>
  );
};

export default PopularProduct;
